import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MessageSquareQuote } from 'lucide-react';
import { SupportNotice } from '@/components/support/SupportResources';
import { MoodPicker, MOOD_STYLE } from './MoodPicker';
import { JournalComposer } from './JournalComposer';
import { JournalEntryRow } from './JournalEntryRow';

const MonthStrip = ({ strip, month }) => {
  const [year, m] = month.split('-').map(Number);
  const days = new Date(year, m, 0).getDate();
  const byDay = Object.fromEntries((strip || []).map((row) => [row.day, row]));

  return (
    <div className="flex flex-wrap gap-1" data-testid="journal-month-strip">
      {Array.from({ length: days }, (_, i) => {
        const day = `${month}-${String(i + 1).padStart(2, '0')}`;
        const row = byDay[day];
        const mood = row?.latest_mood;
        return (
          <div
            key={day}
            title={`${day}${mood ? ' · mood logged' : ''}${row?.wrote ? ' · wrote' : ''}`}
            className={`w-5 h-5 rounded-[5px] border flex items-center justify-center
                        ${mood ? `${MOOD_STYLE[mood].ring} bg-white/[0.06]`
                               : 'border-white/[0.08] bg-white/[0.02]'}`}
            data-testid={`strip-day-${day}`}
          >
            {mood && <span className={`${MOOD_STYLE[mood].fill} rounded-full w-1.5 h-1.5`} />}
            {!mood && row?.wrote && <span className="rounded-full w-1.5 h-1.5 bg-white/40" />}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Their journal. Free writing, prompts, the private worksheets they have already filled in,
 * mood as many times a day as they want, and the choice to hand any of it to a chat.
 */
export const ReflectionJournal = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [channels, setChannels] = useState([]);

  const seed = params.get('content_id') || params.get('prompt')
    ? {
        contentId: params.get('content_id'),
        contentTitle: params.get('content_title'),
        prompt: params.get('prompt'),
      }
    : null;

  const clearSeed = useCallback(() => {
    const next = new URLSearchParams(params);
    ['content_id', 'content_title', 'prompt'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const load = useCallback(() => {
    axios.get(`${API}/companion/journal`)
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], strip: [], month: '' }));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/companion/journal/channels`)
      .then((r) => setChannels(r.data.channels || []))
      .catch(() => setChannels([]));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50" data-testid="journal-loading">Opening your journal…</p>;
  }

  return (
    <div className="space-y-8" data-testid="reflection-journal">
      <SupportNotice />

      {data.quote && (
        <figure className="rounded-2xl border border-violet-300/25 bg-violet-400/[0.06] p-5"
                data-testid="journal-quote">
          <MessageSquareQuote className="w-4 h-4 text-violet-300/70 mb-2" />
          <blockquote className="text-base md:text-lg text-white/90 leading-relaxed">
            {data.quote.text}
          </blockquote>
          <figcaption className="text-xs text-white/40 mt-2">{data.quote.source}</figcaption>
        </figure>
      )}

      <section className="space-y-3" data-testid="journal-today">
        <h2 className="text-base md:text-lg font-semibold text-white">Where are you right now?</h2>
        <p className="text-sm text-white/45">
          As many times a day as you like — a mood is a passing thing, not a daily verdict.
        </p>
        <MoodPicker
          moods={data.moods}
          latest={data.mood?.logged_today ? data.mood.latest : null}
          loggedToday={data.mood?.logged_today}
          onLogged={load}
        />
        {data.mood?.logged_today > 0 && (
          <p className="text-xs text-white/40" data-testid="journal-mood-count">
            {data.mood.logged_today} reading{data.mood.logged_today === 1 ? '' : 's'} today ·
            latest {data.mood.latest_label}
          </p>
        )}
      </section>

      <JournalComposer moods={data.moods} seed={seed} onSaved={load} onClearSeed={clearSeed} />

      {data.month && (
        <section className="space-y-2" data-testid="journal-strip-section">
          <h2 className="text-base md:text-lg font-semibold text-white">This month</h2>
          <MonthStrip strip={data.strip} month={data.month} />
        </section>
      )}

      <section className="space-y-3" data-testid="journal-entries">
        <h2 className="text-base md:text-lg font-semibold text-white">
          Everything you have written
          {data.entries.length > 0 && (
            <span className="text-sm font-normal text-white/40"> · {data.entries.length}</span>
          )}
        </h2>
        {data.entries.length ? (
          <div className="space-y-2">
            {data.entries.map((entry) => (
              <JournalEntryRow
                key={entry.id}
                entry={entry}
                moods={data.moods}
                channels={channels}
                onChanged={load}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="journal-entries-empty">
            Nothing yet. Anything you write here stays private unless you choose to share it —
            and any worksheet you keep private lands here too.
          </p>
        )}
      </section>
    </div>
  );
};

export default ReflectionJournal;
